import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import worldTopo from 'world-atlas/countries-110m.json';

/**
 * Reusable world choropleth (spec §5.3/§9). Values are keyed by world-atlas
 * country name (use `mapCountries.toAtlasCountry`). Countries with data are
 * shaded in the brand blue by relative intensity; hover shows a tooltip and
 * clicking calls `onCountryClick` with the atlas country name (to filter).
 * The topojson is bundled (world-atlas) — no runtime CDN dependency.
 */

interface Props {
  values: Map<string, number>;
  // Tooltip body for a country with a value (e.g. "12 licenses · $4.2M left").
  tooltipFor: (_country: string, _value: number) => string;
  onCountryClick?: (_country: string) => void;
  selected?: string[];
  height?: number;
}

interface Tip {
  name: string;
  body: string;
  x: number;
  y: number;
}

const ACCENT = '72, 149, 239'; // --action-color rgb

const ChoroplethMap: React.FC<Props> = ({
  values,
  tooltipFor,
  onCountryClick,
  selected = [],
  height = 320,
}) => {
  const [tip, setTip] = useState<Tip | null>(null);

  let max = 0;
  values.forEach((v) => {
    if (v > max) max = v;
  });
  const selectedSet = new Set(selected);

  const fillFor = (name: string): string => {
    const v = values.get(name) || 0;
    if (v <= 0) return 'var(--bg-surface)';
    const intensity = max > 0 ? v / max : 0;
    return `rgba(${ACCENT}, ${(0.18 + 0.62 * intensity).toFixed(3)})`;
  };

  return (
    <div className='eh-map-wrap' style={{ height: height }}>
      <ComposableMap
        projection='geoEqualEarth'
        projectionConfig={{ scale: 145 }}
        width={800}
        height={height}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={worldTopo}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const name: string = geo.properties.name;
              const value = values.get(name) || 0;
              const isSel = selectedSet.has(name);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fillFor(name)}
                  stroke='var(--es-soft-border)'
                  strokeWidth={isSel ? 1.4 : 0.5}
                  style={{
                    default: {
                      outline: 'none',
                      stroke: isSel ? 'var(--action-color)' : 'var(--es-soft-border)',
                    },
                    hover: {
                      outline: 'none',
                      fill:
                        value > 0
                          ? `rgba(${ACCENT}, 0.9)`
                          : 'var(--bg-hover)',
                      cursor: value > 0 && onCountryClick ? 'pointer' : 'default',
                    },
                    pressed: { outline: 'none' },
                  }}
                  onMouseMove={(e: React.MouseEvent) =>
                    setTip({
                      name: name,
                      body: value > 0 ? tooltipFor(name, value) : 'No activity',
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                  onMouseLeave={() => setTip(null)}
                  onClick={() => value > 0 && onCountryClick && onCountryClick(name)}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tip && (
        <div
          className='eh-map-tip'
          style={{ left: tip.x + 12, top: tip.y + 12 }}
        >
          <div className='eh-map-tip-name'>{tip.name}</div>
          <div className='eh-map-tip-body'>{tip.body}</div>
        </div>
      )}
    </div>
  );
};

export default ChoroplethMap;
